import { motion } from 'framer-motion';
import { Quote, BadgeCheck, Facebook, Linkedin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Landing-page testimonials — 3-column grid on desktop, stacked on mobile.
 * Content (all 4 locales) lives in LanguageContext under `testimonial.items[]`.
 * Cards use `h-full` inside a stretched grid so heights match even when the
 * quotes are different lengths (design intent: no ragged bottoms).
 */
export default function TestimonialSection() {
  const { t } = useLanguage();
  const items = t('testimonial.items');
  // Defensive: if a translation is missing/malformed, fall back to nothing
  // rather than crashing the landing page.
  const testimonials = Array.isArray(items) ? items : [];

  return (
    <section
      id="testimonial"
      className="py-24 sm:py-32 relative"
      data-testid="testimonial-section"
    >
      {/* Subtle radial glow behind the row */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[420px] pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse 60% 100% at 50% 50%, rgba(90,80,229,0.16) 0%, transparent 65%)',
        }}
        aria-hidden="true"
      />

      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary">
            <BadgeCheck className="w-3 h-3" />
            {t('testimonial.kicker')}
          </span>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-stretch"
          data-testid="testimonial-grid"
        >
          {testimonials.map((item, i) => (
            <TestimonialCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ item, index }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      className="relative h-full rounded-2xl border border-border/60 bg-card px-6 py-10 sm:px-8 sm:py-12 shadow-sm flex flex-col"
      data-testid={`testimonial-card-${index}`}
    >
      {/* Decorative quotation mark */}
      <div
        className="absolute -top-5 left-6 w-11 h-11 rounded-full bg-primary flex items-center justify-center shadow-lg"
        aria-hidden="true"
      >
        <Quote className="w-5 h-5 text-white -scale-x-100" strokeWidth={2.5} />
      </div>

      <blockquote className="flex-1">
        <p
          className="font-heading text-base sm:text-lg leading-relaxed italic text-foreground"
          data-testid={`testimonial-quote-${index}`}
        >
          &ldquo;{item.quote}&rdquo;
        </p>
      </blockquote>

      <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t border-border/40">
        {/* Initials avatar */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-white text-sm shrink-0"
          style={{
            background: 'linear-gradient(135deg, #5A50E5 0%, #7B6FFF 100%)',
          }}
          aria-hidden="true"
          data-testid={`testimonial-avatar-${index}`}
        >
          {item.initials}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="font-heading font-semibold text-foreground truncate"
            data-testid={`testimonial-name-${index}`}
          >
            {item.name}
          </p>
          <div className="flex items-center gap-1.5">
            <p
              className="text-sm text-muted-foreground truncate"
              data-testid={`testimonial-title-${index}`}
            >
              {item.title}
            </p>
            {item.facebook && (
              <a
                href={item.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Facebook — ${item.name}`}
                className="text-muted-foreground/70 hover:text-primary transition-colors shrink-0"
                data-testid={`testimonial-facebook-${index}`}
              >
                <Facebook className="w-3.5 h-3.5" />
              </a>
            )}
            {item.linkedin && (
              <a
                href={item.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`LinkedIn — ${item.name}`}
                className="text-muted-foreground/70 hover:text-primary transition-colors shrink-0"
                data-testid={`testimonial-linkedin-${index}`}
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </figcaption>
    </motion.figure>
  );
}
